import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEscapeLayer } from '@/shared/hooks/useEscapeLayer';
import Tabs from '@/shared/components/ui/Tabs';
import type { Worker } from '../../api/workersApi';
import { StartWorkflowResponse, triggerWorkflowApi } from '../../api/workflowsApi';
import type { OutcomeDefinition, OutcomeValueKind } from '../../api/outcomeDefinitionsApi';
import { useAiText } from '@/features/system/ai/hooks/useAiText';
import { buildHandlerAiUserPrompt, HANDLER_AI_SYSTEM_PROMPT_V2 } from '../../ai/handlerAiPrompt';
import { useWorkflowRunStream } from '../../hooks/useWorkflowRunStream';
import { useJobProgressStream } from '@/features/system/jobs/hooks/useJobProgressStream';

// Avoid direct import.meta access — CRA/webpack treats it as a Critical dependency
// warning, which fails CI builds (CI=true → warnings are errors).
const META_ENV: Record<string, string | undefined> =
  (typeof process !== 'undefined' ? (process.env as Record<string, string | undefined>) : {});

const CREATE_HANDLER_WORKFLOW_KEY =
  META_ENV.VITE_ORCHESTRATOR_CREATE_HANDLER_WORKFLOW_KEY ||
  META_ENV.REACT_APP_ORCHESTRATOR_CREATE_HANDLER_WORKFLOW_KEY ||
  META_ENV.ORCHESTRATOR_CREATE_HANDLER_WORKFLOW_KEY ||
  'system.handler.create';

const CREATE_HANDLER_WITH_AI_WORKFLOW_KEY =
  META_ENV.VITE_ORCHESTRATOR_CREATE_HANDLER_WITH_AI_WORKFLOW_KEY ||
  META_ENV.REACT_APP_ORCHESTRATOR_CREATE_HANDLER_WITH_AI_WORKFLOW_KEY ||
  META_ENV.ORCHESTRATOR_CREATE_HANDLER_WITH_AI_WORKFLOW_KEY ||
  'system.handler.create.ai';

export type CreateHandlerPreset = Partial<{
  handler_key: string;
  worker_id: string;
  handler_namespace: string;
  handler_key_base: string;
  description: string;
  timeout_sec: string;
  max_retries: string;
  backoff_ms: string;
  priority: string;
  tags: string;
  configJson: string;
  is_active: boolean;
  aiSpec: string;
  definitionJson: string;
}>;

interface Props {
  workers: Worker[];
  preset?: CreateHandlerPreset | null;
  aiOpenRequestId?: number;
  onCancel?: () => void;
  onCreated?: () => void;
}

type FormState = {
  worker_id: string;
  handler_namespace: string;
  handler_key_base: string;
  description: string;
  timeout_sec: string;
  max_retries: string;
  backoff_ms: string;
  priority: string;
  tags: string;
  configJson: string;
  is_active: boolean;
  aiSpec: string;
  definitionJson: string;
  metadataJson: string;
};

type HandlerTab = 'Handler' | 'Metadata' | 'IO' | 'Config' | 'Outcomes';

type ParamDefRow = {
  id: string;
  key: string;
  required: boolean;
  type: string;
  description: string;
  defaultJson: string;
};

type OutputDefRow = {
  id: string;
  key: string;
  type: string;
  description: string;
};

type OutcomeDefRow = {
  id: string;
  id_category: number;
  key: string;
  value_kind: OutcomeValueKind;
  description: string;
};

type CreateHandlerWorkflowInputs = {
  worker_key: string;
  handler_namespace: string;
  handler_key_base: string;
  write_handler_registry?: boolean;
  // V2 contract: single config object that may embed handler_config + handler_metadata.
  // Keep legacy fields optional for backward compatibility when pasting older JSON.
  config?: Record<string, unknown>;
  metadata?: {
    handler_metadata?: Record<string, unknown>;
  };
  io?: {
    inputs?: Array<{
      key: string;
      required: boolean;
      type: string;
      description: string;
      default?: unknown;
    }>;
    outputs?: Array<{
      key: string;
      type: string;
      description: string;
    }>;
  };
};

type HandlerIdempotencyConfig = {
  scope_mode?: string;
  scope_namespace?: string;
  scope_key_fields?: string[];
};

function extractIdempotencyFromConfig(config: unknown): {
  scopeMode: string;
  scopeNamespace: string;
  scopeKeyFields: string[];
} {
  const { handlerConfig } = extractHandlerConfigEnvelope(config);
  const base = handlerConfig ?? (config && typeof config === 'object' && !Array.isArray(config) ? (config as Record<string, unknown>) : null);
  if (!base) {
    return { scopeMode: 'none', scopeNamespace: '', scopeKeyFields: [] };
  }
  const raw = (base as Record<string, unknown>).idempotency;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { scopeMode: 'none', scopeNamespace: '', scopeKeyFields: [] };
  }
  const idem = raw as Record<string, unknown>;

  const scopeMode = typeof idem.scope_mode === 'string' ? idem.scope_mode.trim() : 'none';
  const scopeNamespace = typeof idem.scope_namespace === 'string' ? idem.scope_namespace.trim() : '';
  const fields = Array.isArray(idem.scope_key_fields) ? (idem.scope_key_fields as unknown[]) : [];
  const scopeKeyFields = fields
    .filter((x): x is string => typeof x === 'string')
    .map((x) => x.trim())
    .filter(Boolean);

  return {
    scopeMode: scopeMode || 'none',
    scopeNamespace,
    scopeKeyFields,
  };
}

function isCreateHandlerWorkflowInputsLike(value: unknown): value is CreateHandlerWorkflowInputs {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.handler_namespace !== 'string' || typeof v.handler_key_base !== 'string') return false;

  // Accept either:
  // - legacy: {metadata:{handler_metadata}, io:{inputs,outputs}}
  // - v2: {config:{handler_config, handler_metadata}} (or plain handler config)
  const cfg = v.config as any;
  if (cfg && typeof cfg === 'object') return true;

  const meta = v.metadata as any;
  const io = v.io as any;
  if (meta && typeof meta === 'object' && meta.handler_metadata && typeof meta.handler_metadata === 'object') return true;
  if (io && typeof io === 'object' && Array.isArray(io.inputs) && Array.isArray(io.outputs)) return true;
  return false;
}

function isHandlerConfigLike(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (!v.params_schema || typeof v.params_schema !== 'object') return false;
  if (!v.outputs_schema || typeof v.outputs_schema !== 'object') return false;
  // Conservative: require at least params_schema.mandatory/optional or accept any object.
  return true;
}

function extractHandlerConfigEnvelope(value: unknown): {
  handlerConfig: Record<string, unknown> | null;
  handlerMetadata: Record<string, unknown> | null;
} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { handlerConfig: null, handlerMetadata: null };
  }
  const obj = value as Record<string, unknown>;

  // Preferred: { handler_config: {...}, handler_metadata: {...} }
  const hc = obj.handler_config;
  const hm = obj.handler_metadata;
  const handlerConfig = hc && typeof hc === 'object' && !Array.isArray(hc) ? (hc as Record<string, unknown>) : null;
  const handlerMetadata = hm && typeof hm === 'object' && !Array.isArray(hm) ? (hm as Record<string, unknown>) : null;
  if (handlerConfig) return { handlerConfig, handlerMetadata };

  // If already a handler config, treat it as handler_config.
  if (isHandlerConfigLike(obj)) {
    return { handlerConfig: obj, handlerMetadata: null };
  }

  return { handlerConfig: null, handlerMetadata: null };
}

type BaselineSnapshot = {
  form: FormState;
  paramRows: ParamDefRow[];
  outputRows: OutputDefRow[];
  outcomeRows: OutcomeDefRow[];
  handlerKey: string;
};

type DiffKind = 'added' | 'modified' | 'removed' | 'unchanged';

const createDefaultForm = (): FormState => ({
  worker_id  : '',
  handler_namespace: '',
  handler_key_base : '',
  description: '',
  timeout_sec: '300',
  max_retries: '3',
  backoff_ms : '1000',
  priority   : '0',
  tags       : '',
  configJson : '',
  is_active  : true,
  aiSpec     : '',
  definitionJson: '',
  metadataJson: '',
});

function safeJsonParse(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('AI response is empty.');
  try {
    return JSON.parse(trimmed);
  } catch {
    throw new Error('AI response is not valid JSON. Ensure the prompt forces JSON-only output.');
  }
}

function newId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function coerceObject(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};
}

function coerceHandlerMetadata(value: unknown): Record<string, any> {
  const v = coerceObject(value);
  // Accept:
  // - { handler_metadata: {...} }
  // - { metadata: { handler_metadata: {...} } }
  // - { ...fields } (assume it's already handler_metadata)
  if (v.handler_metadata && typeof v.handler_metadata === 'object' && !Array.isArray(v.handler_metadata)) {
    return coerceObject(v.handler_metadata);
  }
  if (v.metadata && typeof v.metadata === 'object' && !Array.isArray(v.metadata)) {
    const m = coerceObject(v.metadata);
    if (m.handler_metadata && typeof m.handler_metadata === 'object' && !Array.isArray(m.handler_metadata)) {
      return coerceObject(m.handler_metadata);
    }
  }
  return v;
}

function tryParseJsonAny(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function tryParseJsonOrString(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

type ValueType = 'integer' | 'floatpoint' | 'string' | 'boolean' | 'jsonb' | 'timestamp';

function normalizeValueType(raw: string): ValueType {
  const t = raw.trim().toLowerCase();
  if (!t) return 'string';

  // Strip legacy prefix.
  const noPrefix = t.startsWith('v_') ? t.slice(2) : t;

  // Accept legacy JSON Schema types and PG-ish aliases.
  switch (noPrefix) {
    case 'int8':
    case 'integer':
      return 'integer';
    case 'float8':
    case 'number':
    case 'floatpoint':
      return 'floatpoint';
    case 'text':
    case 'string':
      return 'string';
    case 'bool':
    case 'boolean':
      return 'boolean';
    case 'jsonb':
    case 'object':
    case 'array':
      return 'jsonb';
    case 'timestamptz':
    case 'timestamp':
      return 'timestamp';
    default:
      return 'string';
  }
}

const OUTCOME_CATEGORY_INPUTS = 10;
const OUTCOME_CATEGORY_OUTPUTS = 20;


function mapValueTypeToOutcomeKind(t: string): OutcomeValueKind {
  switch (normalizeValueType(t)) {
    case 'integer':
      return 'int';
    case 'floatpoint':
      return 'float';
    case 'boolean':
      return 'bool';
    case 'jsonb':
      return 'jsonb';
    case 'timestamp':
      return 'timestamp';
    case 'string':
    default:
      return 'string';
  }
}

function fnv1a32(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function stableIdType(key: string, used: Map<number, string>): number {
  for (let attempt = 0; ; attempt++) {
    const salt = attempt > 0 ? `#${attempt}` : '';
    let id = fnv1a32(key + salt) & 0x7fffffff;
    if (id === 0) id = 1;
    const existing = used.get(id);
    if (existing && existing !== key) continue;
    used.set(id, key);
    return id;
  }
}

function buildGeneratedOutcomeDefinitions(handlerKey: string, rows: OutcomeDefRow[]): OutcomeDefinition[] {
  const hk = handlerKey.trim();
  if (!hk) return [];

  const cleaned = rows
    .map((r) => {
      const cat = Number.isFinite(r.id_category) ? r.id_category : OUTCOME_CATEGORY_OUTPUTS;
      const key = normalizeOutcomeKeyForCategory(r.key, cat);
      return {
        ...r,
        id_category: cat,
        key,
        description: (r.description ?? '').trim(),
      };
    })
    .filter((r) => !!r.key);

  cleaned.sort((a, b) => (a.id_category !== b.id_category ? a.id_category - b.id_category : a.key.localeCompare(b.key)));

  const usedByCategory = new Map<number, Map<number, string>>();
  const out: OutcomeDefinition[] = [];
  for (const r of cleaned) {
    const cat = r.id_category;
    let used = usedByCategory.get(cat);
    if (!used) {
      used = new Map<number, string>();
      usedByCategory.set(cat, used);
    }
    const idType = stableIdType(r.key, used);
    out.push({
      handler_key: hk,
      id_category: cat,
      id_type: idType,
      key: r.key,
      value_kind: r.value_kind,
      description: r.description || '',
    });
  }
  return out;
}

function deriveOutcomeRowsFromIo(params: ParamDefRow[], outputs: OutputDefRow[]): OutcomeDefRow[] {
  const rows: OutcomeDefRow[] = [];

  params
    .map((p) => ({
      ...p,
      key: p.key.trim(),
      description: p.description.trim(),
      type: p.type.trim(),
    }))
    .filter((p) => !!p.key)
    .forEach((p) => {
      rows.push({
        id: newId(),
        id_category: OUTCOME_CATEGORY_INPUTS,
        key: `inputs.${p.key}`,
        value_kind: mapValueTypeToOutcomeKind(p.type),
        description: p.description || (p.required ? `Input ${p.key} (mandatory)` : `Input ${p.key} (optional)`),
      });
    });

  const cleanedOutputs = outputs
    .map((o) => ({ ...o, key: o.key.trim(), description: o.description.trim(), type: o.type.trim() }))
    .filter((o) => !!o.key);

  // Generate outcomes for every declared output key.
  for (const o of cleanedOutputs) {
    const k = o.key;
    if (k === 'result') continue; // handled below to ensure it always exists
    rows.push({
      id: newId(),
      id_category: OUTCOME_CATEGORY_OUTPUTS,
      key: `outputs.${k}`,
      value_kind: mapValueTypeToOutcomeKind(o.type),
      description: o.description || `Output ${k}`,
    });
  }

  // Always include outputs.result as a catch-all.
  const resultOutput = cleanedOutputs.find((o) => o.key === 'result');
  rows.push({
    id: newId(),
    id_category: OUTCOME_CATEGORY_OUTPUTS,
    key: 'outputs.result',
    value_kind: resultOutput ? mapValueTypeToOutcomeKind(resultOutput.type) : 'jsonb',
    description: resultOutput?.description || 'Handler result',
  });

  return rows;
}

function normalizeOutcomeKeyForCategory(key: string, cat: number): string {
  const k = (key ?? '').trim();
  if (!k) return '';

  // If the user already used an explicit prefix, keep it.
  if (k.startsWith('inputs.') || k.startsWith('outputs.')) return k;

  // Keep legacy/special top-level keys as-is.
  if (k === 'inputs' || k === 'result' || k === 'status_code' || k === 'message') return k;

  // Only auto-prefix simple keys (avoid rewriting dotted paths the user might be intentionally typing).
  if (k.includes('.')) return k;

  if (cat === OUTCOME_CATEGORY_INPUTS) return `inputs.${k}`;
  if (cat === OUTCOME_CATEGORY_OUTPUTS) return `outputs.${k}`;
  return k;
}

function extractIoFromConfig(config: unknown): { params: ParamDefRow[]; outputs: OutputDefRow[] } {
  const { handlerConfig } = extractHandlerConfigEnvelope(config);
  const cfg = coerceObject(handlerConfig ?? config);
  const paramsSchema = coerceObject(cfg.params_schema);
  const mandatory = coerceObject(paramsSchema.mandatory);
  const optional = coerceObject(paramsSchema.optional);
  const params: ParamDefRow[] = [];

  const pushParam = (key: string, def: unknown, required: boolean) => {
    const d = coerceObject(def);
    const type = typeof d.type === 'string' ? normalizeValueType(d.type) : '';
    const description = typeof d.description === 'string' ? d.description : '';
    const hasDefault = Object.prototype.hasOwnProperty.call(d, 'default');
    const defaultJson = hasDefault ? JSON.stringify(d.default, null, 0) : '';
    params.push({ id: newId(), key, required, type, description, defaultJson });
  };

  Object.keys(mandatory).sort().forEach((k) => pushParam(k, mandatory[k], true));
  Object.keys(optional).sort().forEach((k) => pushParam(k, optional[k], false));

  const outputs: OutputDefRow[] = [];
  const outputsSchema = coerceObject(cfg.outputs_schema);
  const props = coerceObject(outputsSchema.properties);
  Object.keys(props).sort().forEach((k) => {
    const p = coerceObject(props[k]);
    outputs.push({
      id: newId(),
      key: k,
      type: typeof p.type === 'string' ? normalizeValueType(p.type) : '',
      description: typeof p.description === 'string' ? p.description : '',
    });
  });

  const outputsList = Array.isArray(cfg.outputs) ? cfg.outputs.filter((x) => typeof x === 'string') : [];
  outputsList.forEach((k) => {
    if (!outputs.find((o) => o.key === k)) outputs.push({ id: newId(), key: k, type: '', description: '' });
  });

  return { params, outputs };
}

function buildGeneratedHandlerConfig(
  params: ParamDefRow[],
  outputs: OutputDefRow[],
  runMetadataDefaults?: Record<string, unknown>,
  idempotency?: HandlerIdempotencyConfig
) {
  const mandatory: Record<string, any> = {};
  const optional: Record<string, any> = {};

  params
    .map((p) => ({
      ...p,
      key: p.key.trim(),
      type: p.type.trim(),
      description: p.description.trim(),
      defaultJson: p.defaultJson,
    }))
    .filter((p) => !!p.key)
    .forEach((p) => {
      const def: Record<string, any> = {};
      if (p.type) def.type = normalizeValueType(p.type);
      def.description = p.description || '';
      const parsedDefault = tryParseJsonOrString(p.defaultJson);
      if (parsedDefault !== undefined) def.default = parsedDefault;
      if (p.required) mandatory[p.key] = def;
      else optional[p.key] = def;
    });

  const outputProps: Record<string, any> = {};
  outputs
    .map((o) => ({
      ...o,
      key: o.key.trim(),
      type: o.type.trim(),
      description: o.description.trim(),
    }))
    .filter((o) => !!o.key)
    .forEach((o) => {
      const def: Record<string, any> = {};
      if (o.type) def.type = normalizeValueType(o.type);
      def.description = o.description || '';
      outputProps[o.key] = def;
    });

  const cfg: Record<string, unknown> = {
    params_schema: {
      mandatory,
      optional,
    },
    outputs_schema: {
      $schema: 'https://json-schema.org/draft/2020-12/schema',
      type: 'object',
      additionalProperties: true,
      properties: outputProps,
      required: [],
    },
  };

  if (idempotency && typeof idempotency === 'object') {
    const ns = typeof idempotency.scope_namespace === 'string' ? idempotency.scope_namespace.trim() : '';
    const fields = Array.isArray(idempotency.scope_key_fields)
      ? idempotency.scope_key_fields
          .filter((x): x is string => typeof x === 'string')
          .map((x) => x.trim())
          .filter(Boolean)
      : [];
    const mode = typeof idempotency.scope_mode === 'string' ? idempotency.scope_mode.trim() : '';

    // Persist scope_mode even if scope_key_fields isn't set yet.
    // Orchestrator treats empty fields as "no deterministic scope".
    if (ns) {
      cfg.idempotency = {
        scope_mode: mode || 'upsert',
        scope_namespace: ns,
        scope_key_fields: fields,
      };
    }
  }

  if (runMetadataDefaults && Object.keys(runMetadataDefaults).length > 0) {
    cfg.run_metadata_defaults = runMetadataDefaults;
  }

  return cfg;
}

function normalizeTags(tags: unknown): string {
  if (!Array.isArray(tags)) return '';
  return tags
    .map((t) => (typeof t === 'string' ? t.trim() : ''))
    .filter(Boolean)
    .join(', ');
}

type AiHandlerFields = {
  handler_namespace?: string;
  handler_key_base?: string;
  // Legacy (kept for a short transition; removed from UX)
  data_type?: string;
  operation?: string;
  description: string;
  tags: string[];
  timeout_sec: number;
  max_retries: number;
  backoff_ms: number;
  priority: number;
  is_active: boolean;
  config: unknown;
  outcome_definitions?: {
    definitions: Array<{
      id_category: number;
      key: string;
      value_kind: string;
      description: string;
    }>;
  };
};

function isAiHandlerFields(value: unknown): value is AiHandlerFields {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const hasNew = typeof v.handler_namespace === 'string' && typeof v.handler_key_base === 'string';
  const hasLegacy = typeof v.data_type === 'string' && typeof v.operation === 'string';
  return hasNew || hasLegacy;
}

const normalizeChunk = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/gi, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '');

const buildHandlerKey = (
  workerKey: string | undefined,
  handlerNamespace: string,
  handlerKeyBase: string,
) => {
  const segments: string[] = [];
  if (workerKey?.trim()) segments.push(workerKey.trim());
  const baseChunk = normalizeChunk(handlerKeyBase);
  if (baseChunk) segments.push(baseChunk);
  const nsChunk = normalizeChunk(handlerNamespace);
  if (nsChunk) segments.push(nsChunk);
  const base = segments.join('.');

  if (!base) return '';
  // Create handler is v1-only; version is not user-editable in the UI.
  return `${base}:v1`;
};

const stripVersionSuffix = (handlerKey: string) => handlerKey.replace(/(?:\.v\d+|:v?\d+)$/i, '');

export default function CreateHandlerFormV2({ workers, preset, aiOpenRequestId, onCancel, onCreated }: Props) {
  const aiText = useAiText();
  const [form, setForm] = React.useState<FormState>(() => createDefaultForm());
  const [error, setError] = React.useState<string | null>(null);
  const [aiError, setAiError] = React.useState<string | null>(null);
  const [aiBusy, setAiBusy] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);
  useEscapeLayer(() => { setAiOpen(false); setAiError(null); }, aiOpen && !(preset && aiBusy));
  const [aiCreateRunId, setAiCreateRunId] = React.useState<string | null>(null);
  const [aiCreateModel, setAiCreateModel] = React.useState<string>('');
  const [aiCreateTemperature, setAiCreateTemperature] = React.useState<string>('');
  const [aiCreateMaxTokens, setAiCreateMaxTokens] = React.useState<string>('');
  const [aiCreateReferenceConfigsJson, setAiCreateReferenceConfigsJson] = React.useState<string>('');
  const [aiCreateCodeReference, setAiCreateCodeReference] = React.useState<string>('');
  const [aiCreateResultHandlerKey, setAiCreateResultHandlerKey] = React.useState<string | null>(null);
  const [diffActive, setDiffActive] = React.useState(false);
  const [tab, setTab] = React.useState<HandlerTab>('Handler');
  const [, setIdempotencyScopeNamespace] = React.useState<string>('');
  const [idempotencyScopeMode, setIdempotencyScopeMode] = React.useState<string>('none');
  const [scopeKeyFields, setScopeKeyFields] = React.useState<string[]>([]);
  const [paramRows, setParamRows] = React.useState<ParamDefRow[]>([]);
  const [outputRows, setOutputRows] = React.useState<OutputDefRow[]>([]);
  const [runMetadataDefaults, setRunMetadataDefaults] = React.useState<Record<string, unknown> | undefined>(undefined);
  const baselineRef = React.useRef<BaselineSnapshot | null>(null);
  const qc = useQueryClient();

  const isUpdateMode = !!preset;

  const normalizeText = (value: unknown) => String(value ?? '').trim();

  const diffRowClass = (kind: DiffKind) => {
    if (!diffActive) return '';
    switch (kind) {
      case 'added':
        return 'border-l-4 border-emerald-600 bg-emerald-950/20 text-emerald-200';
      case 'modified':
        return 'border-l-4 border-indigo-600 bg-indigo-950/20 text-indigo-200';
      case 'removed':
        return 'border-l-4 border-rose-600 bg-rose-950/10 text-rose-200';
      case 'unchanged':
      default:
        return 'border-l-4 border-transparent';
    }
  };

  const resetToCreateState = React.useCallback(() => {
    baselineRef.current = null;
    setDiffActive(false);
    setForm(createDefaultForm());
    setIdempotencyScopeNamespace('');
    setIdempotencyScopeMode('none');
    setScopeKeyFields([]);
    setParamRows([]);
    setOutputRows([]);
    setRunMetadataDefaults(undefined);
    setTab('Handler');
    setError(null);
    setAiError(null);
  }, []);

  // Outcomes are read-only and must always mirror IO.
  // Derive them directly from the current IO rows (no separate state to drift).
  const outcomeRows = React.useMemo(() => deriveOutcomeRowsFromIo(paramRows, outputRows), [paramRows, outputRows]);

  const selectedWorker = React.useMemo(
    () => workers.find((worker) => String(worker.worker_id) === form.worker_id),
    [workers, form.worker_id]
  );

  // Scope namespace is derived deterministically from the handler identity.
  // This field is read-only in the UI to keep scopes stable and predictable.
  const computedScopeNamespace = React.useMemo(() => {
    const wk = normalizeChunk(String(selectedWorker?.worker_key ?? ''));
    const base = normalizeChunk(String(form.handler_key_base ?? ''));
    if (!wk || !base) return '';
    return `${wk}.${base}`;
  }, [selectedWorker?.worker_key, form.handler_key_base]);

  const effectiveScopeNamespace = React.useMemo(() => {
    return computedScopeNamespace;
  }, [computedScopeNamespace]);

  const handlerKey = React.useMemo(
    () => buildHandlerKey(selectedWorker?.worker_key, form.handler_namespace, form.handler_key_base),
    [selectedWorker?.worker_key, form.handler_namespace, form.handler_key_base]
  );

  // Outcomes definitions are treated as structural contract for the handler family,
  // so we store them under the versionless key (e.g. math.foo.bar).
  const outcomesHandlerKey = React.useMemo(() => stripVersionSuffix(handlerKey), [handlerKey]);

  React.useEffect(() => {
    if (!baselineRef.current) return;
    if (!handlerKey.trim()) return;
    if (!baselineRef.current.handlerKey) baselineRef.current.handlerKey = handlerKey;
  }, [handlerKey]);

  React.useEffect(() => {
    if (!preset) {
      resetToCreateState();
      return;
    }

    const nextForm = { ...createDefaultForm(), ...preset };
    setForm(() => nextForm);
    setError(null);
    setAiError(null);

    const parsed = typeof nextForm.configJson === 'string' ? tryParseJsonAny(nextForm.configJson) : null;
    if (parsed && typeof parsed === 'object') {
      const io = extractIoFromConfig(parsed);
      const derivedOutcomes = deriveOutcomeRowsFromIo(io.params, io.outputs);
      setParamRows(io.params);
      setOutputRows(io.outputs);

      const rmd = coerceObject((parsed as any)?.run_metadata_defaults);
      setRunMetadataDefaults(Object.keys(rmd).length > 0 ? rmd : undefined);

      const idem = extractIdempotencyFromConfig(parsed);
      setIdempotencyScopeNamespace(idem.scopeNamespace);
      setIdempotencyScopeMode(idem.scopeMode || 'none');
      setScopeKeyFields(idem.scopeKeyFields);

      baselineRef.current = {
        form: nextForm,
        paramRows: io.params,
        outputRows: io.outputs,
        outcomeRows: derivedOutcomes,
        handlerKey: '',
      };
    } else {
      setParamRows([]);
      setOutputRows([]);
      setIdempotencyScopeNamespace('');
      setIdempotencyScopeMode('none');
      setScopeKeyFields([]);
      setRunMetadataDefaults(undefined);

      baselineRef.current = {
        form: nextForm,
        paramRows: [],
        outputRows: [],
        outcomeRows: [],
        handlerKey: '',
      };
    }

    setDiffActive(false);
  }, [preset, resetToCreateState]);

  React.useEffect(() => {
    if (form.worker_id || workers.length === 0) return;
    setForm((prev) => ({ ...prev, worker_id: String(workers[0].worker_id) }));
  }, [workers, form.worker_id]);

  const mutation = useMutation<StartWorkflowResponse, Error, CreateHandlerWorkflowInputs>({
    mutationFn: (inputs) => triggerWorkflowApi({
      // Versioned handler entries: even when editing an existing handler, we create a new version.
      workflow_key: CREATE_HANDLER_WORKFLOW_KEY,
      inputs: inputs as unknown as Record<string, unknown>,
    }),
    onError: (err) => setError(err?.message || 'Failed to register handler'),
  });

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyAiFieldsToForm = (payload: AiHandlerFields) => {
    setForm((prev) => {
      const next: FormState = { ...prev };
      // Guardrail: during updates, never let AI implicitly rename the handler.
      // handler_key is derived from worker_key + handler_key_base + handler_namespace.
      // Renames should be explicit user edits.
      if (!preset) {
        const ns = (payload.handler_namespace ?? payload.data_type ?? '').trim();
        const base = (payload.handler_key_base ?? payload.operation ?? '').trim();
        next.handler_namespace = ns || prev.handler_namespace;
        next.handler_key_base = base || prev.handler_key_base;
      }

      if (payload.description !== undefined) {
        next.description = String(payload.description ?? '');
      }
      next.timeout_sec = String(payload.timeout_sec ?? prev.timeout_sec);
      next.max_retries = String(payload.max_retries ?? prev.max_retries);
      next.backoff_ms = String(payload.backoff_ms ?? prev.backoff_ms);
      next.priority = String(payload.priority ?? prev.priority);
      next.tags = normalizeTags(payload.tags);
      next.is_active = payload.is_active ?? true;

      if (payload.config !== undefined) {
        const env = extractHandlerConfigEnvelope(payload.config);
        const cfgForForm = env.handlerConfig ?? (payload.config as any);
        next.configJson = JSON.stringify(cfgForForm, null, 2);

        // If the user is using the new single-JSON contract, keep metadata alongside.
        // Only fill metadataJson if user hasn't already provided one.
        if (env.handlerMetadata && !String(prev.metadataJson ?? '').trim()) {
          next.metadataJson = JSON.stringify(env.handlerMetadata, null, 2);
        }
      }
      return next;
    });

    if (payload.config && typeof payload.config === 'object') {
      const env = extractHandlerConfigEnvelope(payload.config);
      const cfgForIo = env.handlerConfig ?? (payload.config as any);
      const io = extractIoFromConfig(cfgForIo);
      setParamRows(io.params);
      setOutputRows(io.outputs);

      const rmd = coerceObject((cfgForIo as any)?.run_metadata_defaults);
      setRunMetadataDefaults(Object.keys(rmd).length > 0 ? rmd : undefined);

      const idem = extractIdempotencyFromConfig(cfgForIo);
      setIdempotencyScopeNamespace(idem.scopeNamespace);
      setIdempotencyScopeMode(idem.scopeMode || 'none');
      setScopeKeyFields(idem.scopeKeyFields);
    }
  };

  const applyWorkflowInputsToForm = (payload: CreateHandlerWorkflowInputs) => {
    // Map workflow-style inputs into the form/IO rows.
    const workerKey = String((payload as any).worker_key ?? '').trim();
    if (workerKey) {
      const match = workers.find((w) => String(w.worker_key).trim() === workerKey);
      if (match) {
        setField('worker_id', String(match.worker_id));
      } else {
        // Not fatal: user can pick the worker manually.
        setAiError(`Unknown worker_key '${workerKey}'. Select a worker manually.`);
      }
    }

    setForm((prev) => {
      const next: FormState = { ...prev };
      if (!preset) {
        next.handler_namespace = String(payload.handler_namespace ?? '').trim() || prev.handler_namespace;
        next.handler_key_base = String(payload.handler_key_base ?? '').trim() || prev.handler_key_base;
      }

      const metaRaw =
        (payload as any)?.config?.handler_metadata ??
        (payload as any)?.metadata?.handler_metadata ??
        (payload as any)?.metadata;
      const hm = coerceHandlerMetadata(metaRaw);
      if (hm.description !== undefined) next.description = String(hm.description ?? '').trim();
      if (hm.timeout_sec !== undefined) next.timeout_sec = String(hm.timeout_sec ?? '');
      if (hm.max_retries !== undefined) next.max_retries = String(hm.max_retries ?? '');
      if (hm.backoff_ms !== undefined) next.backoff_ms = String(hm.backoff_ms ?? '');
      if (hm.priority !== undefined) next.priority = String(hm.priority ?? '');
      if (hm.tags !== undefined) next.tags = normalizeTags(hm.tags);
      if (hm.is_active !== undefined) next.is_active = !!hm.is_active;
      return next;
    });

    const cfgRaw = (payload as any)?.config;
    const env = extractHandlerConfigEnvelope(cfgRaw);
    const cfg = env.handlerConfig ?? cfgRaw;
    const idem = extractIdempotencyFromConfig(cfg);
    setIdempotencyScopeNamespace(idem.scopeNamespace);
    setIdempotencyScopeMode(idem.scopeMode || 'none');
    setScopeKeyFields(idem.scopeKeyFields);

    const inputsRaw = Array.isArray((payload as any)?.io?.inputs) ? ((payload as any).io.inputs as any[]) : [];
    const outputsRaw = Array.isArray((payload as any)?.io?.outputs) ? ((payload as any).io.outputs as any[]) : [];

    // If IO wasn't provided (v2 config-only), derive it from handler_config.
    if (inputsRaw.length === 0 && outputsRaw.length === 0 && cfg && typeof cfg === 'object') {
      const io = extractIoFromConfig(cfg);
      setParamRows(io.params);
      setOutputRows(io.outputs);
      return;
    }

    const nextParams: ParamDefRow[] = inputsRaw
      .filter((x) => x && typeof x === 'object')
      .map((x) => {
        const key = String((x as any).key ?? '').trim();
        const def = (x as any).default;
        return {
          id: newId(),
          key,
          required: (x as any).required !== false,
          type: String((x as any).type ?? 'string'),
          description: String((x as any).description ?? ''),
          defaultJson: def === undefined ? '' : JSON.stringify(def),
        };
      })
      .filter((x) => x.key);

    const nextOutputs: OutputDefRow[] = outputsRaw
      .filter((x) => x && typeof x === 'object')
      .map((x) => ({
        id: newId(),
        key: String((x as any).key ?? '').trim(),
        type: String((x as any).type ?? 'string'),
        description: String((x as any).description ?? ''),
      }))
      .filter((x) => x.key);

    setParamRows(nextParams);
    setOutputRows(nextOutputs);
  };

  const applyHandlerMetadataToForm = React.useCallback((raw: unknown) => {
    const hm = coerceHandlerMetadata(raw);
    if (!hm || typeof hm !== 'object') return;

    setForm((prev) => {
      const next: FormState = { ...prev };
      if (hm.description !== undefined) next.description = String(hm.description ?? '').trim();
      if (hm.timeout_sec !== undefined) next.timeout_sec = String(hm.timeout_sec ?? '').trim() || prev.timeout_sec;
      if (hm.max_retries !== undefined) next.max_retries = String(hm.max_retries ?? '').trim() || prev.max_retries;
      if (hm.backoff_ms !== undefined) next.backoff_ms = String(hm.backoff_ms ?? '').trim() || prev.backoff_ms;
      if (hm.priority !== undefined) next.priority = String(hm.priority ?? '').trim() || prev.priority;
      if (hm.tags !== undefined) next.tags = normalizeTags(hm.tags);
      if (hm.is_active !== undefined) next.is_active = !!hm.is_active;
      return next;
    });
  }, []);

  const lastAppliedDefinitionJson = React.useRef<string>('');
  React.useEffect(() => {
    const raw = String(form.definitionJson ?? '').trim();
    if (!raw) return;
    if (raw === lastAppliedDefinitionJson.current) return;

    const parsed = tryParseJsonAny(raw);
    if (!isCreateHandlerWorkflowInputsLike(parsed)) return;

    lastAppliedDefinitionJson.current = raw;
    setAiError(null);
    applyWorkflowInputsToForm(parsed);
  }, [form.definitionJson]);

  const lastAppliedMetadataJson = React.useRef<string>('');
  React.useEffect(() => {
    const raw = String(form.metadataJson ?? '').trim();
    if (!raw) return;
    if (raw === lastAppliedMetadataJson.current) return;

    const parsed = tryParseJsonAny(raw);
    if (!parsed || typeof parsed !== 'object') return;

    lastAppliedMetadataJson.current = raw;
    setAiError(null);
    applyHandlerMetadataToForm(parsed);
  }, [applyHandlerMetadataToForm, form.metadataJson]);

  const typeOptions = React.useMemo(
    () => [
      { label: '—', value: '' },
      { label: 'integer', value: 'integer' },
      { label: 'floatpoint', value: 'floatpoint' },
      { label: 'string', value: 'string' },
      { label: 'boolean', value: 'boolean' },
      { label: 'jsonb', value: 'jsonb' },
      { label: 'timestamp', value: 'timestamp' },
    ],
    []
  );

  const generatedConfig = React.useMemo(
    () => {
      const available = new Set(
        paramRows
          .map((r) => r.key.trim())
          .filter(Boolean)
      );
      const fields = scopeKeyFields
        .map((k) => k.trim())
        .filter((k) => !!k && available.has(k));

      const mode = (idempotencyScopeMode || 'none').trim().toLowerCase();

      const idempotency: HandlerIdempotencyConfig | undefined =
        mode !== 'none' && effectiveScopeNamespace.trim()
          ? {
              scope_mode: mode,
              scope_namespace: effectiveScopeNamespace.trim(),
              scope_key_fields: fields,
            }
          : undefined;

      return buildGeneratedHandlerConfig(paramRows, outputRows, runMetadataDefaults, idempotency);
    },
    [paramRows, outputRows, effectiveScopeNamespace, idempotencyScopeMode, scopeKeyFields, runMetadataDefaults]
  );

  const generatedConfigJson = React.useMemo(
    () => JSON.stringify(generatedConfig, null, 2),
    [generatedConfig]
  );

  const generatedOutcomeDefs = React.useMemo(
    () => buildGeneratedOutcomeDefinitions(outcomesHandlerKey, outcomeRows),
    [outcomesHandlerKey, outcomeRows]
  );

  const generatedOutcomeJson = React.useMemo(
    () => JSON.stringify({ definitions: generatedOutcomeDefs }, null, 2),
    [generatedOutcomeDefs]
  );

  React.useEffect(() => {
    setForm((prev) => (prev.configJson === generatedConfigJson ? prev : { ...prev, configJson: generatedConfigJson }));
  }, [generatedConfigJson]);

  const lastAiOpenRequestId = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (typeof aiOpenRequestId !== 'number') return;
    if (aiOpenRequestId <= 0) return;
    if (lastAiOpenRequestId.current === aiOpenRequestId) return;
    lastAiOpenRequestId.current = aiOpenRequestId;
    setAiError(null);
    setAiOpen(true);
  }, [aiOpenRequestId]);

  // Two AI states:
  // 1) Create: handler does not exist yet (no preset provided)
  // 2) Update: handler exists and we loaded it into the form (preset provided)
  const aiMode = preset ? 'update' : 'fill';

  const aiDialogTitle = aiMode === 'update' ? 'Update handler with AI' : 'Create handler with AI (workflow)';
  const aiDialogHint = aiMode === 'update'
    ? 'Paste changes or updated spec. The AI must return JSON only and will update/merge with the current contract.'
    : `Starts workflow ${CREATE_HANDLER_WITH_AI_WORKFLOW_KEY} to analyze code and register the handler.`;

  const aiRunStream = useWorkflowRunStream(aiCreateRunId);

  const aiActiveStep = React.useMemo(() => {
    const steps = aiRunStream.snapshot?.steps;
    if (!Array.isArray(steps) || steps.length === 0) return null;
    const norm = (v: any) => String(v ?? '').trim().toUpperCase();
    const inProgress = steps.find((s) => {
      const st = norm((s as any)?.status);
      return st === 'RUNNING' || st === 'READY' || st === 'PENDING';
    });
    return inProgress || steps[0] || null;
  }, [aiRunStream.snapshot]);

  const aiActiveJobId = (aiActiveStep as any)?.job_id ? String((aiActiveStep as any).job_id) : null;
  const aiJobProgress = useJobProgressStream(aiActiveJobId, { useSSE: true, fallbackPollMs: 3000, autoStopOnDone: false });

  const findFirstStringField = React.useCallback((value: any, fieldName: string): string | null => {
    const seen = new Set<any>();
    const stack: any[] = [value];
    while (stack.length > 0) {
      const cur = stack.pop();
      if (cur && typeof cur === 'object') {
        if (seen.has(cur)) continue;
        seen.add(cur);
        if (typeof (cur as any)[fieldName] === 'string' && String((cur as any)[fieldName]).trim()) {
          return String((cur as any)[fieldName]).trim();
        }
        if (Array.isArray(cur)) {
          for (const item of cur) stack.push(item);
        } else {
          for (const k of Object.keys(cur)) stack.push((cur as any)[k]);
        }
      }
    }
    return null;
  }, []);

  React.useEffect(() => {
    // When the AI dialog is opened in create mode, reset the workflow-run state.
    if (!aiOpen) return;
    if (aiMode !== 'fill') return;
    setAiCreateRunId(null);
    setAiCreateResultHandlerKey(null);
    setAiError(null);
  }, [aiOpen, aiMode]);

  React.useEffect(() => {
    if (aiMode !== 'fill') return;
    const snap = aiRunStream.snapshot;
    const status = String(snap?.status ?? '').toUpperCase();
    if (!status) return;

    if (status === 'SUCCEEDED') {
      const handlerKey =
        findFirstStringField(snap, 'handler_key') ||
        findFirstStringField(snap, 'handlerKey');

      if (handlerKey) setAiCreateResultHandlerKey(handlerKey);

      // Refresh handler list for the left pane.
      qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'handlers'] });
      qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'handlers', 'v2'] });

      // Keep the dialog open so the user can see the result; allow manual close.
      setAiBusy(false);
    }

    if (status === 'FAILED' || status === 'ERROR' || status === 'CANCELLED' || status === 'CANCELED') {
      setAiBusy(false);
      const msg =
        snap?.steps?.find((s: any) => String(s?.last_error ?? '').trim())?.last_error ||
        (snap as any)?.run?.last_error ||
        (snap as any)?.summary?.last_error ||
        (snap as any)?.run?.error ||
        (snap as any)?.summary?.error;
      setAiError(String(msg || 'AI workflow failed (no error details provided by orchestrator).'));
    }
  }, [aiMode, aiRunStream.snapshot, findFirstStringField, qc]);

  const ioDiff = React.useMemo(() => {
    const baseline = baselineRef.current;
    if (!baseline) {
      return {
        params: new Map<string, DiffKind>(),
        outputs: new Map<string, DiffKind>(),
        removedParams: [] as string[],
        removedOutputs: [] as string[],
      };
    }

    const baseParams = new Map<string, ParamDefRow>();
    for (const p of baseline.paramRows) {
      const k = p.key.trim();
      if (k) baseParams.set(k, p);
    }
    const baseOutputs = new Map<string, OutputDefRow>();
    for (const o of baseline.outputRows) {
      const k = o.key.trim();
      if (k) baseOutputs.set(k, o);
    }

    const params = new Map<string, DiffKind>();
    const outputs = new Map<string, DiffKind>();

    for (const row of paramRows) {
      const k = row.key.trim();
      if (!k) continue;
      const b = baseParams.get(k);
      if (!b) {
        params.set(row.id, 'added');
        continue;
      }
      const changed =
        b.required !== row.required ||
        normalizeText(b.type) !== normalizeText(row.type) ||
        normalizeText(b.description) !== normalizeText(row.description) ||
        normalizeText(b.defaultJson) !== normalizeText(row.defaultJson);
      params.set(row.id, changed ? 'modified' : 'unchanged');
    }

    for (const row of outputRows) {
      const k = row.key.trim();
      if (!k) continue;
      const b = baseOutputs.get(k);
      if (!b) {
        outputs.set(row.id, 'added');
        continue;
      }
      const changed =
        normalizeText(b.type) !== normalizeText(row.type) ||
        normalizeText(b.description) !== normalizeText(row.description);
      outputs.set(row.id, changed ? 'modified' : 'unchanged');
    }

    const currentParamKeys = new Set(paramRows.map((r) => r.key.trim()).filter(Boolean));
    const currentOutputKeys = new Set(outputRows.map((r) => r.key.trim()).filter(Boolean));
    const removedParams = baseline.paramRows.map((r) => r.key.trim()).filter((k) => k && !currentParamKeys.has(k));
    const removedOutputs = baseline.outputRows.map((r) => r.key.trim()).filter((k) => k && !currentOutputKeys.has(k));

    return { params, outputs, removedParams, removedOutputs };
  }, [paramRows, outputRows]);

  const outcomesDiff = React.useMemo(() => {
    const baseline = baselineRef.current;
    if (!baseline) {
      return {
        outcomes: new Map<string, DiffKind>(),
        removedOutcomes: [] as Array<{ id_category: number; key: string }>,
      };
    }

    const base = new Map<string, OutcomeDefRow>();
    for (const o of baseline.outcomeRows) {
      const k = o.key.trim();
      if (!k) continue;
      base.set(`${o.id_category}:${k}`, o);
    }

    const outcomes = new Map<string, DiffKind>();
    const currentKeys = new Set<string>();
    for (const row of outcomeRows) {
      const k = row.key.trim();
      if (!k) continue;
      const key = `${row.id_category}:${k}`;
      currentKeys.add(key);
      const b = base.get(key);
      if (!b) {
        outcomes.set(row.id, 'added');
        continue;
      }
      const changed =
        normalizeText(b.value_kind) !== normalizeText(row.value_kind) ||
        normalizeText(b.description) !== normalizeText(row.description);
      outcomes.set(row.id, changed ? 'modified' : 'unchanged');
    }

    const removedOutcomes = baseline.outcomeRows
      .map((o) => ({ id_category: o.id_category, key: o.key.trim() }))
      .filter((o) => o.key && !currentKeys.has(`${o.id_category}:${o.key}`));

    return { outcomes, removedOutcomes };
  }, [outcomeRows]);

  const removedOutcomeRows = React.useMemo(() => {
    const baseline = baselineRef.current;
    if (!diffActive || !baseline) {
      return { removedInputs: [] as OutcomeDefRow[], removedOutputs: [] as OutcomeDefRow[] };
    }

    const currentKeys = new Set<string>();
    for (const row of outcomeRows) {
      const k = row.key.trim();
      if (!k) continue;
      currentKeys.add(`${row.id_category}:${k}`);
    }

    const removed = baseline.outcomeRows
      .map((r) => ({ ...r, key: r.key.trim() }))
      .filter((r) => r.key && !currentKeys.has(`${r.id_category}:${r.key}`))
      .map((r) => ({ ...r, id: `removed:${r.id_category}:${r.key}` }));

    return {
      removedInputs: removed.filter((r) => r.id_category === OUTCOME_CATEGORY_INPUTS),
      removedOutputs: removed.filter((r) => r.id_category === OUTCOME_CATEGORY_OUTPUTS),
    };
  }, [diffActive, outcomeRows]);

  const handleFillWithAi = async () => {
    setAiError(null);
    setError(null);

    const spec = form.aiSpec.trim();
    const definition = String(form.definitionJson ?? '').trim();
    const metaJson = String(form.metadataJson ?? '').trim();
    if (!spec && !definition) {
      setAiError('Paste a handler spec (plain text) or Definition (JSON) first.');
      return;
    }

    // Always apply metadata JSON if provided (best-effort), regardless of other paths.
    if (metaJson) {
      const parsedMeta = tryParseJsonAny(metaJson);
      if (parsedMeta && typeof parsedMeta === 'object') {
        try {
          applyHandlerMetadataToForm(parsedMeta);
        } catch {
          // Ignore: metadata parsing is best-effort and should not block the main flow.
        }
      }
    }

    // If the user pasted JSON, treat it as a direct fill (no AI call).
    const pastedJson = tryParseJsonAny(spec || definition);
    if (pastedJson && typeof pastedJson === 'object') {
      try {
        if (isCreateHandlerWorkflowInputsLike(pastedJson)) {
          applyWorkflowInputsToForm(pastedJson);
          if (aiMode === 'update') setDiffActive(true);
          setAiOpen(false);
          return;
        }
        if (isAiHandlerFields(pastedJson)) {
          applyAiFieldsToForm(pastedJson);
          if (aiMode === 'update') setDiffActive(true);
          setAiOpen(false);
          return;
        }

        // Allow pasting the new envelope: { handler_config, handler_metadata }
        const env = extractHandlerConfigEnvelope(pastedJson);
        if (env.handlerConfig) {
          if (env.handlerMetadata) {
            try {
              applyHandlerMetadataToForm(env.handlerMetadata);
            } catch {
              // best-effort
            }
          }

          setForm((prev) => ({ ...prev, configJson: JSON.stringify(env.handlerConfig, null, 2) }));

          const rmd = coerceObject((env.handlerConfig as any)?.run_metadata_defaults);
          setRunMetadataDefaults(Object.keys(rmd).length > 0 ? rmd : undefined);

          const idem = extractIdempotencyFromConfig(env.handlerConfig);
          setIdempotencyScopeNamespace(idem.scopeNamespace);
          setIdempotencyScopeMode(idem.scopeMode || 'upsert');
          setScopeKeyFields(idem.scopeKeyFields);

          const io = extractIoFromConfig(env.handlerConfig);
          setParamRows(io.params);
          setOutputRows(io.outputs);

          if (aiMode === 'update') setDiffActive(true);
          setAiOpen(false);
          return;
        }

        // Allow pasting the handler config object directly (params_schema/outputs_schema).
        // This is the "real config" used by the handler registry.
        if (isHandlerConfigLike(pastedJson)) {
          // Best-effort: some users paste a superset object that includes handler metadata.
          // Hydrate description if present so it doesn't look like AI ignored it.
          const cfgObj = pastedJson as Record<string, unknown>;
          const metaDesc = (cfgObj as any)?.metadata?.handler_metadata?.description;
          const desc = typeof metaDesc === 'string' ? metaDesc : (cfgObj as any)?.description;
          if (typeof desc === 'string' && desc.trim()) {
            setForm((prev) => (prev.description.trim() ? prev : ({ ...prev, description: desc.trim() })));
          }

          setForm((prev) => ({ ...prev, configJson: JSON.stringify(pastedJson, null, 2) }));

          const rmd = coerceObject((pastedJson as any)?.run_metadata_defaults);
          setRunMetadataDefaults(Object.keys(rmd).length > 0 ? rmd : undefined);

          const idem = extractIdempotencyFromConfig(pastedJson);
          setIdempotencyScopeNamespace(idem.scopeNamespace);
          setIdempotencyScopeMode(idem.scopeMode || 'upsert');
          setScopeKeyFields(idem.scopeKeyFields);

          const io = extractIoFromConfig(pastedJson as any);
          setParamRows(io.params);
          setOutputRows(io.outputs);

          if (aiMode === 'update') setDiffActive(true);
          setAiOpen(false);
          return;
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setAiError(msg || 'Invalid JSON payload.');
        setError(msg || 'Failed to register handler');
        return;
      }
    }

    // No JSON fill path; require plain-text spec for AI.
    if (!spec) {
      setAiError('Paste a handler spec (plain text) for AI generation, or paste a JSON definition that the form can apply.');
      return;
    }

    setAiBusy(true);
    try {
      const systemPrompt = HANDLER_AI_SYSTEM_PROMPT_V2;

      const existingDraftJson = {
        handler_namespace: form.handler_namespace.trim() || '',
        handler_key_base: form.handler_key_base.trim() || '',
        description: form.description.trim() || '',
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        timeout_sec: Number.parseInt(form.timeout_sec.trim() || '0', 10) || 0,
        max_retries: Number.parseInt(form.max_retries.trim() || '0', 10) || 0,
        backoff_ms: Number.parseInt(form.backoff_ms.trim() || '0', 10) || 0,
        priority: Number.parseInt(form.priority.trim() || '0', 10) || 0,
        is_active: !!form.is_active,
        config: generatedConfig,
      };

      const userPrompt = buildHandlerAiUserPrompt(spec, {
        mode: aiMode,
        existingDraftJson: aiMode === 'update' ? existingDraftJson : undefined,
      });

      if (typeof systemPrompt !== 'string' || !systemPrompt.trim()) {
        throw new Error('Internal error: system prompt is missing.');
      }
      if (typeof userPrompt !== 'string' || !userPrompt.trim()) {
        throw new Error('Internal error: user prompt is missing.');
      }

      const res = await aiText.generate({
        system_prompt: systemPrompt,
        user_prompt: userPrompt,
        temperature: 0.2,
        max_output_tokens: 2048,
      });
      if (!res) return; // 202 async dispatch — result via SSE

      const json = safeJsonParse(res.content);
      if (!isAiHandlerFields(json)) {
        throw new Error('AI response JSON does not look like handler form fields (missing handler_namespace/handler_key_base).');
      }

      applyAiFieldsToForm(json);
      if (aiMode === 'update') setDiffActive(true);
      setAiOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setAiError(msg || 'AI generation failed.');
    } finally {
      setAiBusy(false);
    }
  };

  const handleCreateWithAiWorkflow = async () => {
    setAiError(null);
    setError(null);

    const codeReference = String(aiCreateCodeReference ?? '').trim();
    if (!codeReference) {
      setAiError('Paste handler implementation/source code into Code reference first.');
      return;
    }

    const handlerNamespace = normalizeChunk(form.handler_namespace);
    const handlerKeyBase = normalizeChunk(form.handler_key_base);
    if (!handlerNamespace) {
      setAiError('Handler namespace is required (fill it in on the main form).');
      return;
    }
    if (!handlerKeyBase) {
      setAiError('Handler key base is required (fill it in on the main form).');
      return;
    }

    const workerId = Number.parseInt(String(form.worker_id || '').trim(), 10);
    if (!Number.isFinite(workerId) || workerId <= 0) {
      setAiError('Select a worker on the main form (worker_key is required by the workflow).');
      return;
    }
    const selectedWorker = workers.find((w) => String(w.worker_id) === String(workerId));
    const workerKey = String(selectedWorker?.worker_key ?? '').trim();
    if (!workerKey) {
      setAiError('Selected worker is missing worker_key.');
      return;
    }

    const model = String(aiCreateModel ?? '').trim();
    const tempRaw = String(aiCreateTemperature ?? '').trim();
    const maxTokRaw = String(aiCreateMaxTokens ?? '').trim();
    const refJsonRaw = String(aiCreateReferenceConfigsJson ?? '').trim();

    let temperature: number | undefined;
    if (tempRaw) {
      const v = Number.parseFloat(tempRaw);
      if (!Number.isFinite(v)) {
        setAiError('Temperature must be a number.');
        return;
      }
      temperature = v;
    }

    let max_output_tokens: number | undefined;
    if (maxTokRaw) {
      const v = Number.parseInt(maxTokRaw, 10);
      if (!Number.isFinite(v) || v < 0) {
        setAiError('Max output tokens must be an integer >= 0.');
        return;
      }
      max_output_tokens = v;
    }

    let reference_configs: any = undefined;
    if (refJsonRaw) {
      const parsed = tryParseJsonAny(refJsonRaw);
      if (parsed == null) {
        setAiError('Reference configs must be valid JSON (e.g. an array of example configs).');
        return;
      }
      reference_configs = parsed;
    }

    const inputs: Record<string, unknown> = {
      code_reference: codeReference,
      // Required by the system.handler.create.ai workflow schema and used to build the handler key.
      worker_key: workerKey,
      handler_key_base: handlerKeyBase,
      handler_namespace: handlerNamespace,
      // Required by the workflow schema; not currently edited in the dialog.
      metadata: '{}',
    };
    if (model) inputs.model = model;
    if (temperature != null) inputs.temperature = temperature;
    if (max_output_tokens != null) inputs.max_output_tokens = max_output_tokens;
    if (reference_configs != null) inputs.reference_configs = reference_configs;

    setAiBusy(true);
    try {
      const started = await triggerWorkflowApi({
        workflow_key: CREATE_HANDLER_WITH_AI_WORKFLOW_KEY,
        version: 0,
        inputs,
      });
      setAiCreateRunId(String(started.workflow_run_id || '').trim() || null);
      setAiCreateResultHandlerKey(null);
    } catch (err: any) {
      setAiBusy(false);
      setAiError(String(err?.message || err || 'Failed to start AI workflow'));
    }
  };

  const parseIntField = (value: string) => {
    if (!value?.trim()) return undefined;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const buildPayload = (): CreateHandlerWorkflowInputs | null => {
    const workerId = Number.parseInt(form.worker_id, 10);
    if (!Number.isFinite(workerId) || workerId <= 0) {
      setError('Select a worker to attach to this handler.');
      return null;
    }

    const selected = workers.find((w) => String(w.worker_id) === String(workerId));
    const workerKey = String(selected?.worker_key ?? '').trim();
    if (!workerKey) {
      setError('Selected worker is missing worker_key.');
      return null;
    }

    // V2: always send a single config envelope that includes handler_config + handler_metadata.
    // This is required for step-scoped idempotency (stored on handler_config.idempotency).
    const parsedConfig = generatedConfig as Record<string, unknown>;

    const tags = form.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    if (!handlerKey) {
      setError('Fill in worker, namespace, and handler key base to build a handler key.');
      return null;
    }

    const handlerNamespace = normalizeChunk(form.handler_namespace);
    const handlerKeyBase = normalizeChunk(form.handler_key_base);
    if (!handlerNamespace) {
      setError('Handler namespace is required.');
      return null;
    }
    if (!handlerKeyBase) {
      setError('Handler key base is required.');
      return null;
    }

    const handlerMetadata: Record<string, unknown> = {
      description: form.description.trim() || undefined,
      timeout_sec: parseIntField(form.timeout_sec),
      max_retries: parseIntField(form.max_retries),
      backoff_ms: parseIntField(form.backoff_ms),
      priority: parseIntField(form.priority),
      tags: tags.length ? tags : undefined,
      is_active: form.is_active,
    };

    // Remove undefined keys to keep the config tidy.
    Object.keys(handlerMetadata).forEach((k) => {
      if (handlerMetadata[k] === undefined) delete handlerMetadata[k];
    });

    const payload: CreateHandlerWorkflowInputs = {
      worker_key: workerKey,
      handler_namespace: handlerNamespace,
      handler_key_base: handlerKeyBase,
      // Persist to orchestrator handler-registry by default (durable catalog).
      // Opt-out can be supported later if needed.
      write_handler_registry: true,
      config: {
        handler_config: parsedConfig,
        handler_metadata: handlerMetadata,
      },
    };

    return payload;
  };

  const canSave =
    handlerKey.trim().length > 0 &&
    !!form.worker_id &&
    !mutation.isPending &&
    workers.length > 0;

  const handleConfirm = () => {
    (async () => {
      setError(null);
      if (!canSave) return;
      const payload = buildPayload();
      if (!payload) return;

      try {
        await mutation.mutateAsync(payload);

        // Create is workflow-driven; the handler may appear a moment after the workflow is started.
        qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'handlers'] });
        window.setTimeout(() => {
          qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'handlers'] });
        }, 1500);

        setForm(createDefaultForm());
        setIdempotencyScopeNamespace('');
        setIdempotencyScopeMode('upsert');
        setScopeKeyFields([]);
        setParamRows([]);
        setOutputRows([]);
        setTab('Handler');
        setError(null);
        onCreated?.();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg || 'Failed to register handler');
      }
    })();
  };

  return (
    <div className="h-full min-h-0 flex flex-col gap-4">
      {error && <p className="text-sm text-rose-400 shrink-0">{error}</p>}

      <div className="flex items-center justify-between gap-3 shrink-0">
        <Tabs
          tabs={['Handler', 'Metadata', 'IO', 'Outcomes', 'Config']}
          activeTab={tab}
          onTabChange={(t) => setTab(t as HandlerTab)}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-auto pr-1">
        {tab === 'Handler' && (
          <div className="space-y-4">
            <LabeledSelect
              label="Worker *"
              value={form.worker_id}
              onChange={(value) => setField('worker_id', value)}
              options={workers.map((worker) => ({
                label: `${worker.worker_key} (${worker.transport})`,
                value: String(worker.worker_id),
              }))}
              placeholder={workers.length === 0 ? 'No workers available' : 'Select worker'}
            />

            <div className="flex items-center justify-between gap-3 rounded pac-glass px-3 py-2">
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-400">AI</div>
                <div className="text-[11px] text-neutral-500">Generate handler fields/IO using a plain-text spec.</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setAiError(null);
                  setAiOpen(true);
                }}
                className="rounded border border-neutral-700 px-3 py-1 text-xs text-neutral-200 hover:border-neutral-500 hover:text-neutral-50"
                title="Paste a handler spec and generate JSON"
              >
                {aiMode === 'update' ? 'Update with AI' : 'Create with AI'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LabeledInput
                label="Handler key base"
                value={form.handler_key_base}
                placeholder="worker"
                onChange={(value) => setField('handler_key_base', value)}
                description="Middle segment of the handler key (e.g. system.worker.create → key base = worker)."
              />
              <LabeledInput
                label="Handler namespace"
                value={form.handler_namespace}
                placeholder="create"
                onChange={(value) => setField('handler_namespace', value)}
                description="Last segment of the handler key (e.g. system.worker.create → namespace = create)."
              />
            </div>

          <LabeledInput
            label="Handler Key"
            value={handlerKey}
            placeholder="<worker>.<base>.<namespace>:v1"
            onChange={() => {}}
            readOnly
            description={selectedWorker ? `Derived from ${selectedWorker.worker_key} + base + namespace (v1).` : 'Select a worker to generate the prefix.'}
          />
          </div>
        )}

        {tab === 'Metadata' && (
          <div className="space-y-4">
            <LabeledTextarea
              label="Description"
              value={form.description}
              placeholder="Optional notes for other operators"
              onChange={(value) => setField('description', value)}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LabeledInput
                label="Timeout (seconds)"
                type="number"
                value={form.timeout_sec}
                onChange={(value) => setField('timeout_sec', value)}
              />
              <LabeledInput
                label="Max retries"
                type="number"
                value={form.max_retries}
                onChange={(value) => setField('max_retries', value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LabeledInput
                label="Backoff (ms)"
                type="number"
                value={form.backoff_ms}
                onChange={(value) => setField('backoff_ms', value)}
              />
              <LabeledInput
                label="Priority"
                type="number"
                value={form.priority}
                onChange={(value) => setField('priority', value)}
              />
            </div>

            <LabeledInput
              label="Tags (comma separated)"
              value={form.tags}
              placeholder="beta, nightly"
              onChange={(value) => setField('tags', value)}
            />

            <label className="flex items-center gap-2 text-sm text-neutral-200">
              <input
                type="checkbox"
                className="accent-emerald-500"
                checked={form.is_active}
                onChange={(e) => setField('is_active', e.target.checked)}
              />
              Handler is active immediately
            </label>

            <div className="rounded pac-glass p-3">
              <div className="text-xs uppercase tracking-wide text-neutral-400">Idempotency (Step Scope)</div>
              <div className="text-[11px] leading-snug text-neutral-500 mt-1">
                Configure how the orchestrator derives a deterministic <span className="text-neutral-300">id_scope</span> per step.
                This is stored in <span className="text-neutral-300">config.idempotency</span> in the handler registry.
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <LabeledInput
                  label="Scope namespace"
                  value={effectiveScopeNamespace}
                  placeholder="<worker_key>.<handler_key_base>"
                  onChange={() => {}}
                  disabled
                  description="Auto-derived from worker_key + handler_key_base (stable discriminator)."
                />
                <LabeledSelect
                  label="Scope mode"
                  value={idempotencyScopeMode}
                  onChange={(value) => setIdempotencyScopeMode(value)}
                  options={[
                    { label: 'none (default)', value: 'none' },
                    { label: 'upsert', value: 'upsert' },
                    { label: 'create', value: 'create' },
                  ]}
                  placeholder="Select mode"
                />
              </div>
            </div>
          </div>
        )}

        {tab === 'IO' && (
          <div className="space-y-4">
          <div className="rounded pac-glass p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-400">Inputs / Params</div>
                <div className="text-[11px] leading-snug text-neutral-500">
                  These become step params in the Workflow Builder. Defaults are optional; required + no default forces wiring in the workflow.
                </div>
              </div>
              <button
                type="button"
                className="rounded border border-neutral-800 px-2 py-1 text-xs text-neutral-200 hover:border-neutral-600"
                  onClick={() => setParamRows((prev) => [...prev, { id: newId(), key: '', required: true, type: '', description: '', defaultJson: '' }])}
              >
                Add param
              </button>
            </div>

            <div className="mt-3 space-y-2">
              {paramRows.length === 0 && <div className="text-sm text-neutral-500">No params defined.</div>}
              {paramRows.map((row) => {
                const kind = ioDiff.params.get(row.id) ?? 'unchanged';
                const keyTrim = row.key.trim();
                return (
                  <div key={row.id} className={`rounded ${diffRowClass(diffActive ? kind : 'unchanged')}`}>
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs uppercase tracking-wide text-neutral-400">Key</label>
                          <input
                            value={row.key}
                            onChange={(e) => {
                              const nextRaw = e.target.value;
                              const nextTrim = nextRaw.trim();
                              setParamRows((prev) => prev.map((p) => (p.id === row.id ? { ...p, key: nextRaw } : p)));
                              if (keyTrim && keyTrim !== nextTrim) {
                                setScopeKeyFields((prev) => {
                                  if (!prev.includes(keyTrim)) return prev;
                                  const set = new Set(prev);
                                  set.delete(keyTrim);
                                  if (nextTrim) set.add(nextTrim);
                                  return Array.from(set.values());
                                });
                              }
                            }}
                            placeholder="param_name"
                            className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs uppercase tracking-wide text-neutral-400">Type</label>
                          <select
                            value={row.type}
                            onChange={(e) =>
                              setParamRows((prev) => prev.map((p) => (p.id === row.id ? { ...p, type: e.target.value } : p)))
                            }
                            className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                          >
                            {typeOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-span-4">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs uppercase tracking-wide text-neutral-400">Description</label>
                          <input
                            value={row.description}
                            onChange={(e) =>
                              setParamRows((prev) =>
                                prev.map((p) => (p.id === row.id ? { ...p, description: e.target.value } : p))
                              )
                            }
                            placeholder="What this param controls"
                            className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs uppercase tracking-wide text-neutral-400">DEFAULT VALUE</label>
                          <input
                            value={row.defaultJson}
                            onChange={(e) =>
                              setParamRows((prev) =>
                                prev.map((p) => (p.id === row.id ? { ...p, defaultJson: e.target.value } : p))
                              )
                            }
                            placeholder="Optional"
                            className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm font-mono outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                          <div className="text-[11px] leading-snug text-neutral-500">Leave blank for no default</div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs uppercase tracking-wide text-neutral-400">Req</label>
                          <label className="flex items-center gap-2 text-sm text-neutral-200">
                            <input
                              type="checkbox"
                              className="accent-emerald-500"
                              checked={row.required}
                              onChange={(e) =>
                                setParamRows((prev) =>
                                  prev.map((p) => (p.id === row.id ? { ...p, required: e.target.checked } : p))
                                )
                              }
                            />
                          </label>

                          <label className="text-xs uppercase tracking-wide text-neutral-400 mt-1">Scope</label>
                          <label className="flex items-center gap-2 text-sm text-neutral-200">
                            <input
                              type="checkbox"
                              className="accent-indigo-500"
                              disabled={!keyTrim}
                              checked={!!keyTrim && scopeKeyFields.includes(keyTrim)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (!keyTrim) return;
                                setScopeKeyFields((prev) => {
                                  const set = new Set(prev);
                                  if (checked) set.add(keyTrim);
                                  else set.delete(keyTrim);
                                  return Array.from(set.values());
                                });
                              }}
                            />
                          </label>

                          <button
                            type="button"
                            className="mt-1 rounded border border-neutral-800 px-2 py-1 text-xs text-neutral-200 hover:border-neutral-600"
                            onClick={() => {
                              if (keyTrim) {
                                setScopeKeyFields((prev) => prev.filter((k) => k.trim() !== keyTrim));
                              }
                              setParamRows((prev) => prev.filter((p) => p.id !== row.id));
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {diffActive && ioDiff.removedParams.length > 0 ? (
                <div className="rounded border border-rose-800 bg-rose-950/10 p-2 text-xs text-rose-100">
                  <div className="font-semibold">Removed inputs</div>
                  <div className="mt-1 text-rose-200">{ioDiff.removedParams.join(', ')}</div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded pac-glass p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-400">Outputs</div>
                <div className="text-[11px] leading-snug text-neutral-500">
                  These keys become available as upstream bindings in the Workflow Builder.
                </div>
              </div>
              <button
                type="button"
                className="rounded border border-neutral-800 px-2 py-1 text-xs text-neutral-200 hover:border-neutral-600"
                  onClick={() => setOutputRows((prev) => [...prev, { id: newId(), key: '', type: '', description: '' }])}
              >
                Add output
              </button>
            </div>

            <div className="mt-3 space-y-2">
              {outputRows.length === 0 && <div className="text-sm text-neutral-500">No outputs defined.</div>}
              {outputRows.map((row) => {
                const kind = ioDiff.outputs.get(row.id) ?? 'unchanged';
                return (
                  <div key={row.id} className={`rounded ${diffRowClass(diffActive ? kind : 'unchanged')}`}>
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-4">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs uppercase tracking-wide text-neutral-400">Key</label>
                          <input
                            value={row.key}
                            onChange={(e) =>
                              setOutputRows((prev) => prev.map((o) => (o.id === row.id ? { ...o, key: e.target.value } : o)))
                            }
                            placeholder="result"
                            className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                      <div className="col-span-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs uppercase tracking-wide text-neutral-400">Type</label>
                          <select
                            value={row.type}
                            onChange={(e) =>
                              setOutputRows((prev) => prev.map((o) => (o.id === row.id ? { ...o, type: e.target.value } : o)))
                            }
                            className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                          >
                            {typeOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-span-4">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs uppercase tracking-wide text-neutral-400">Description</label>
                          <input
                            value={row.description}
                            onChange={(e) =>
                              setOutputRows((prev) =>
                                prev.map((o) => (o.id === row.id ? { ...o, description: e.target.value } : o))
                              )
                            }
                            placeholder="What this output means"
                            className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs uppercase tracking-wide text-neutral-400">&nbsp;</label>
                          <button
                            type="button"
                            className="rounded border border-neutral-800 px-2 py-1 text-xs text-neutral-200 hover:border-neutral-600"
                            onClick={() => setOutputRows((prev) => prev.filter((o) => o.id !== row.id))}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {diffActive && ioDiff.removedOutputs.length > 0 ? (
                <div className="rounded border border-rose-800 bg-rose-950/10 p-2 text-xs text-rose-100">
                  <div className="font-semibold">Removed outputs</div>
                  <div className="mt-1 text-rose-200">{ioDiff.removedOutputs.join(', ')}</div>
                </div>
              ) : null}
            </div>
          </div>
          </div>
        )}

        {tab === 'Config' && (
          <div className="h-full min-h-0 flex flex-col gap-2">
          <LabeledTextarea
            label="Config (JSON) · Read only"
            value={generatedConfigJson}
            placeholder={generatedConfigJson}
            onChange={() => {}}
            containerClassName="flex-1 min-h-0"
            textareaClassName="flex-1 min-h-0 font-mono"
            readOnly
          />

          <LabeledTextarea
            label="Outcome definitions (JSON) · Read only"
            value={generatedOutcomeJson}
            placeholder={generatedOutcomeJson}
            onChange={() => {}}
            containerClassName="flex-1 min-h-0"
            textareaClassName="flex-1 min-h-0 font-mono"
            readOnly
          />
          </div>
        )}

        {tab === 'Outcomes' && (
          <div className="space-y-3">
            <div className="rounded pac-glass p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-neutral-400">Define outcomes</div>
                  <div className="text-[11px] leading-snug text-neutral-500">
                    Outcomes are read-only here and reflect the handler’s configured contract.
                  </div>
                </div>
              </div>

              {!handlerKey.trim() && (
                <div className="mt-3 text-sm text-neutral-400">
                  Fill worker, namespace, and handler key base to define outcomes.
                </div>
              )}

              {handlerKey.trim() && (
                <div className="mt-3 space-y-3">
                  {outcomeRows.length === 0 && <div className="text-sm text-neutral-500">No outcomes defined yet.</div>}

                  {outcomeRows.length > 0 && (
                    <>
                      <div className="rounded pac-glass p-3">
                        <div className="text-xs uppercase tracking-wide text-neutral-400">Inputs</div>
                        <div className="mt-2 grid grid-cols-12 gap-2 items-start text-xs uppercase tracking-wide text-neutral-400">
                          <div className="col-span-6">Key</div>
                          <div className="col-span-2">Kind</div>
                          <div className="col-span-4">Description</div>
                        </div>
                        <div className="mt-2 space-y-2">
                          {outcomeRows
                            .filter((r) => r.id_category === OUTCOME_CATEGORY_INPUTS)
                            .map((row) => {
                              const kind = outcomesDiff.outcomes.get(row.id) ?? 'unchanged';
                              return (
                                <div key={row.id} className={`rounded px-2 py-2 ${diffRowClass(diffActive ? kind : 'unchanged')}`}>
                                  <div className="grid grid-cols-12 gap-2 items-start">
                                    <div className="col-span-6 text-sm text-neutral-100">{row.key}</div>
                                    <div className="col-span-2 text-xs text-neutral-300">{row.value_kind}</div>
                                    <div className="col-span-4 text-[11px] leading-snug text-neutral-500">{row.description}</div>
                                  </div>
                                </div>
                              );
                            })}

                          {removedOutcomeRows.removedInputs.map((row) => (
                            <div key={row.id} className={`rounded px-2 py-2 ${diffRowClass('removed')}`}>
                              <div className="grid grid-cols-12 gap-2 items-start">
                                <div className="col-span-6 text-sm text-neutral-100">{row.key}</div>
                                <div className="col-span-2 text-xs text-neutral-300">{row.value_kind}</div>
                                <div className="col-span-4 text-[11px] leading-snug text-neutral-500">{row.description}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="rounded pac-glass p-3">
                        <div className="text-xs uppercase tracking-wide text-neutral-400">Outputs</div>
                        <div className="mt-2 grid grid-cols-12 gap-2 items-start text-xs uppercase tracking-wide text-neutral-400">
                          <div className="col-span-6">Key</div>
                          <div className="col-span-2">Kind</div>
                          <div className="col-span-4">Description</div>
                        </div>
                        <div className="mt-2 space-y-2">
                          {outcomeRows
                            .filter((r) => r.id_category === OUTCOME_CATEGORY_OUTPUTS)
                            .map((row) => {
                              const kind = outcomesDiff.outcomes.get(row.id) ?? 'unchanged';
                              return (
                                <div key={row.id} className={`rounded px-2 py-2 ${diffRowClass(diffActive ? kind : 'unchanged')}`}>
                                  <div className="grid grid-cols-12 gap-2 items-start">
                                    <div className="col-span-6 text-sm text-neutral-100">{row.key}</div>
                                    <div className="col-span-2 text-xs text-neutral-300">{row.value_kind}</div>
                                    <div className="col-span-4 text-[11px] leading-snug text-neutral-500">{row.description}</div>
                                  </div>
                                </div>
                              );
                            })}

                          {removedOutcomeRows.removedOutputs.map((row) => (
                            <div key={row.id} className={`rounded px-2 py-2 ${diffRowClass('removed')}`}>
                              <div className="grid grid-cols-12 gap-2 items-start">
                                <div className="col-span-6 text-sm text-neutral-100">{row.key}</div>
                                <div className="col-span-2 text-xs text-neutral-300">{row.value_kind}</div>
                                <div className="col-span-4 text-[11px] leading-snug text-neutral-500">{row.description}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {diffActive && outcomesDiff.removedOutcomes.length > 0 ? (
                    <div className="rounded border border-rose-800 bg-rose-950/10 p-2 text-xs text-rose-100">
                      <div className="font-semibold">Removed outcomes</div>
                      <div className="mt-1 text-rose-200">
                        {outcomesDiff.removedOutcomes.map((o) => `${o.id_category}:${o.key}`).join(', ')}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-neutral-800 shrink-0">
        <button
          type="button"
          className="rounded border border-neutral-700 px-3 py-1 text-sm text-neutral-200 hover:border-neutral-500"
          onClick={() => {
            setError(null);
            setAiError(null);
            setAiOpen(false);
            setParamRows([]);
            setOutputRows([]);
            setTab('Handler');
            if (onCancel) onCancel();
            else setForm(createDefaultForm());
          }}
          disabled={mutation.isPending}
        >
          Cancel
        </button>
        <button
          type="button"
          className={
            'rounded border px-3 py-1 text-sm ' +
            (canSave
              ? 'border-emerald-600 bg-emerald-600/20 text-emerald-100 hover:bg-emerald-600/30'
              : 'border-neutral-800 pac-glass text-neutral-500')
          }
          onClick={handleConfirm}
          disabled={!canSave}
        >
          {mutation.isPending
            ? isUpdateMode
              ? 'Creating new version…'
              : 'Creating…'
            : isUpdateMode
              ? 'Create new version'
              : 'Create handler'}
        </button>
      </div>

      {aiOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4"
          role="dialog"
          aria-modal="true"
          aria-label={aiDialogTitle}
        >
          <div className="w-full max-w-3xl rounded-lg border border-neutral-800 bg-black p-4 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-neutral-100">{aiDialogTitle}</div>
                <div className="text-xs text-neutral-400">{aiDialogHint}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  // Close does not cancel workflow runs; it only dismisses the dialog.
                  if (aiMode === 'update' && aiBusy) return;
                  setAiOpen(false);
                  setAiError(null);
                }}
                className="rounded border border-neutral-700 bg-black px-2 py-1 text-xs text-neutral-200 hover:bg-rose-600 hover:border-rose-600 hover:text-white transition-colors disabled:opacity-60"
                disabled={aiMode === 'update' && aiBusy}
                title={aiBusy ? 'Generating…' : 'Close'}
              >
                Close
              </button>
            </div>

            {aiError && <p className="mt-3 text-sm text-amber-300">{aiError}</p>}

            {aiMode === 'fill' ? (
              <>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <LabeledInput
                    label="Workflow"
                    value={CREATE_HANDLER_WITH_AI_WORKFLOW_KEY}
                    onChange={() => {}}
                    readOnly
                    description="This workflow calls AI to derive IO/metadata and registers the handler."
                  />
                  <LabeledInput
                    label="Model (optional)"
                    value={aiCreateModel}
                    onChange={setAiCreateModel}
                    placeholder="gpt-4.1-mini"
                  />
                  <LabeledInput
                    label="Temperature (optional)"
                    value={aiCreateTemperature}
                    onChange={setAiCreateTemperature}
                    placeholder="0.2"
                  />
                  <LabeledInput
                    label="Max output tokens (optional)"
                    value={aiCreateMaxTokens}
                    onChange={setAiCreateMaxTokens}
                    placeholder="1200"
                  />
                </div>

                <div className="mt-3">
                  <LabeledTextarea
                    label="Reference configs (optional, JSON)"
                    value={aiCreateReferenceConfigsJson}
                    placeholder='[ { "example": true } ]'
                    onChange={setAiCreateReferenceConfigsJson}
                    description="Optional JSON (typically an array) with example handler configs to guide the AI."
                  />
                </div>

                <div className="mt-3">
                  <LabeledTextarea
                    label="Code reference"
                    value={aiCreateCodeReference}
                    placeholder="Paste the handler implementation source code here…"
                    onChange={setAiCreateCodeReference}
                    textareaClassName="min-h-[180px] font-mono"
                    description="Required. This is sent as inputs.code_reference to the workflow."
                  />
                </div>

                {aiCreateRunId && (
                  <div className="mt-3 rounded pac-glass p-3">
                    <div className="text-xs text-neutral-300">Run: <span className="text-neutral-100">{aiCreateRunId}</span></div>
                    <div className="mt-1 text-xs text-neutral-400">
                      Status: <span className="text-neutral-200">{String(aiRunStream.snapshot?.status || (aiRunStream.loading ? 'RUNNING' : 'PENDING'))}</span>
                      {typeof aiRunStream.snapshot?.percent === 'number' ? ` • ${aiRunStream.snapshot?.percent}%` : ''}
                    </div>

                    {aiActiveStep?.step_id && (
                      <div className="mt-2 text-xs text-neutral-400">
                        Step: <span className="text-neutral-200">{String((aiActiveStep as any).step_id)}</span>
                        {(aiActiveStep as any)?.job_id ? <span className="text-neutral-500"> • job {String((aiActiveStep as any).job_id)}</span> : null}
                      </div>
                    )}

                    {aiActiveJobId && aiJobProgress.latest && (
                      <div className="mt-1 text-xs text-neutral-300">
                        Progress: <span className="text-neutral-100">{String((aiJobProgress.latest as any).message || (aiJobProgress.latest as any).stage || '')}</span>
                        {typeof (aiJobProgress.latest as any).pct === 'number' ? <span className="text-neutral-500"> • {(aiJobProgress.latest as any).pct}%</span> : null}
                      </div>
                    )}

                    {(() => {
                      const steps = Array.isArray(aiRunStream.snapshot?.steps) ? aiRunStream.snapshot?.steps : null;
                      if (!steps || steps.length === 0) return null;
                      return (
                      <div className="mt-2 space-y-1 text-xs">
                        {steps.map((s: any) => (
                          <div key={String(s?.step_id || s?.id || Math.random())} className="flex gap-2">
                            <div className="w-40 shrink-0 text-neutral-300">{String(s?.step_id || 'step')}</div>
                            <div className="w-20 shrink-0 text-neutral-400">{String(s?.status || '')}</div>
                            {String(s?.last_error || '').trim()
                              ? <div className="min-w-0 flex-1 truncate text-rose-300">{String(s?.last_error)}</div>
                              : null}
                          </div>
                        ))}
                      </div>
                      );
                    })()}

                    {aiCreateResultHandlerKey && (
                      <div className="mt-2 text-xs text-emerald-300">Created handler: {aiCreateResultHandlerKey}</div>
                    )}
                    {aiError && (
                      <div className="mt-2 text-xs text-rose-300">{String(aiError)}</div>
                    )}
                    {aiRunStream.error && (
                      <div className="mt-2 text-xs text-rose-300">{String(aiRunStream.error?.message || aiRunStream.error)}</div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="mt-3">
                  <LabeledTextarea
                    label="Handler spec (plain text)"
                    value={form.aiSpec}
                    placeholder="Paste a plain-text spec. The AI must return JSON only."
                    onChange={(value) => setField('aiSpec', value)}
                  />
                </div>

                <div className="mt-3">
                  <LabeledTextarea
                    label="Definition (JSON)"
                    value={form.definitionJson}
                    placeholder={
                      '{\n'
                      + '  "worker_key": "system",\n'
                      + '  "handler_key_base": "download",\n'
                      + '  "handler_namespace": "sea_level",\n'
                      + '  "metadata": { "handler_metadata": { "description": "..." } },\n'
                      + '  "io": { "inputs": [], "outputs": [] }\n'
                      + '}'
                    }
                    onChange={(value) => setField('definitionJson', value)}
                    description="Paste JSON to fill the form: either (1) create-handler workflow inputs, or (2) a handler config object (params_schema/outputs_schema)."
                  />
                </div>

                <div className="mt-3">
                  <LabeledTextarea
                    label="Metadata (JSON)"
                    value={form.metadataJson}
                    placeholder={
                      '{\n'
                      + '  "handler_metadata": {\n'
                      + '    "description": "...",\n'
                      + '    "timeout_sec": 300,\n'
                      + '    "max_retries": 3,\n'
                      + '    "backoff_ms": 1000,\n'
                      + '    "priority": 0,\n'
                      + '    "tags": ["tag1"],\n'
                      + '    "is_active": true\n'
                      + '  }\n'
                      + '}'
                    }
                    onChange={(value) => setField('metadataJson', value)}
                    description="Optional. Paste handler metadata JSON to populate the Handler/Metadata tabs (description, timeouts, tags, is_active)."
                  />
                </div>
              </>
            )}

            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded border border-neutral-700 bg-black px-3 py-2 text-xs text-neutral-200 hover:bg-rose-600 hover:border-rose-600 hover:text-white transition-colors disabled:opacity-60"
                disabled={aiMode === 'update' ? aiBusy : false}
                onClick={() => {
                  if (aiMode === 'update' && aiBusy) return;
                  setAiOpen(false);
                  setAiError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded border border-neutral-700 bg-black px-3 py-2 text-xs text-neutral-300 hover:bg-emerald-600 hover:border-emerald-600 hover:text-white transition-colors disabled:opacity-60"
                onClick={aiMode === 'fill' ? handleCreateWithAiWorkflow : handleFillWithAi}
                disabled={aiMode === 'update' ? aiBusy : false}
              >
                {aiMode === 'fill'
                  ? (aiBusy ? 'Starting…' : (aiCreateRunId ? 'Start again' : 'Create'))
                  : (aiBusy ? 'Generating…' : 'Generate & fill')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface LabeledInputProps {
  label: string;
  value: string;
  placeholder?: string;
  type?: string;
  onChange: (value: string) => void;
  description?: string;
  readOnly?: boolean;
  disabled?: boolean;
}

function LabeledInput({ label, value, placeholder, type = 'text', onChange, description, readOnly, disabled }: LabeledInputProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs uppercase tracking-wide text-neutral-400">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-70"
        readOnly={readOnly}
        disabled={!!disabled || !!readOnly}
      />
      {description && <p className="text-[11px] leading-snug text-neutral-500">{description}</p>}
    </div>
  );
}

interface LabeledSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ label: string; value: string }>;
  placeholder?: string;
}

function LabeledSelect({ label, value, onChange, options, placeholder }: LabeledSelectProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs uppercase tracking-wide text-neutral-400">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
      >
        {value === '' && <option value="" disabled>{placeholder ?? 'Select'}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

interface LabeledTextareaProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  description?: string;
  containerClassName?: string;
  textareaClassName?: string;
  readOnly?: boolean;
}

function LabeledTextarea({ label, value, placeholder, onChange, description, containerClassName, textareaClassName, readOnly }: LabeledTextareaProps) {
  return (
    <div className={`flex flex-col gap-1 ${containerClassName ?? ''}`}>
      <label className="text-xs uppercase tracking-wide text-neutral-400">{label}</label>
      <textarea
        value={value}
        onChange={(e) => {
          if (!readOnly) onChange(e.target.value);
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`bg-black border border-neutral-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500 min-h-[80px] ${readOnly ? 'opacity-80' : ''} ${textareaClassName ?? ''}`}
      />
      {description && <p className="text-[11px] leading-snug text-neutral-500">{description}</p>}
    </div>
  );
}
